/**
 * ============================================
 * SUCURSAL SELECTOR - Selector de Sucursal Activa
 * ============================================
 *
 * Componente dropdown para cambiar la sucursal activa.
 * Muestra la sucursal actual y permite seleccionar otra.
 *
 * Características:
 * - Muestra código y nombre de la sucursal
 * - Dropdown con todas las sucursales disponibles
 * - Persiste la selección automáticamente
 * - Admin Global puede ver "Todas las sucursales"
 */

import { Building2, Check, ChevronsUpDown, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSucursal, ALL_SUCURSALES, ALL_SUCURSALES_ID } from "@/contexts/sucursal-context";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";

export function SucursalSelector() {
  const { t } = useTranslation();
  const {
    sucursales,
    sucursalActiva,
    isLoading,
    setSucursalActiva,
    canViewAllSucursales,
    isAllSucursalesSelected,
  } = useSucursal();

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-[180px]" />
      </div>
    );
  }

  // No sucursales available
  if (sucursales.length === 0) {
    return null;
  }

  // Single sucursal and not admin - just show it, no dropdown
  if (sucursales.length === 1 && !canViewAllSucursales && sucursalActiva) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
        <Building2 className="h-4 w-4" />
        <span className="font-medium">{sucursalActiva.codigo}</span>
        <span className="hidden sm:inline">- {sucursalActiva.nombre}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2 px-3"
          title={t("sucursal.selectBranch")}
        >
          {isAllSucursalesSelected ? (
            <Globe className="h-4 w-4" />
          ) : (
            <Building2 className="h-4 w-4" />
          )}
          {sucursalActiva ? (
            isAllSucursalesSelected ? (
              <span className="font-medium">{t("sucursal.allBranches")}</span>
            ) : (
              <>
                <span className="font-medium">{sucursalActiva.codigo}</span>
                <span className="hidden sm:inline text-muted-foreground">
                  - {sucursalActiva.nombre}
                </span>
              </>
            )
          ) : (
            <span>{t("sucursal.selectBranch")}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[260px]">
        <DropdownMenuLabel>{t("sucursal.availableBranches")}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Opción "Todas las sucursales" solo para Admin Global */}
        {canViewAllSucursales && (
          <>
            <DropdownMenuItem
              onSelect={() => setSucursalActiva(ALL_SUCURSALES)}
              className="flex items-center gap-2 cursor-pointer"
            >
              {isAllSucursalesSelected ? (
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
              ) : (
                <div className="w-4 flex-shrink-0" />
              )}
              <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="font-medium">{t("sucursal.allBranches")}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        {sucursales.map((sucursal) => {
          const isSelected =
            sucursalActiva?.id === sucursal.id && sucursalActiva.id !== ALL_SUCURSALES_ID;
          const isInactive = !sucursal.is_active;

          return (
            <DropdownMenuItem
              key={sucursal.id}
              onSelect={() => setSucursalActiva(sucursal)}
              className={`flex items-center gap-2 cursor-pointer ${isInactive ? "opacity-60" : ""}`}
            >
              {isSelected ? (
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
              ) : (
                <div className="w-4 flex-shrink-0" />
              )}
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`font-medium text-sm ${isInactive ? "line-through text-muted-foreground" : ""}`}
                  >
                    {sucursal.nombre}
                  </span>
                  {isInactive && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {t("common.inactive")}
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{sucursal.codigo}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
